// Dependency imports
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconButton, Paper, Stack } from '@mui/material';
import CircularProgress, {
  CircularProgressProps
} from '@mui/material/CircularProgress';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import AddToQueueIcon from '@mui/icons-material/AddToQueue';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

// Local imports
import CircularWithValueLabel from 'components/progress/circularProgress';
import { Check } from 'components/Form';
import DeleteAction from './deleteAction';
import SelectConfig from '../../assets/images/selectConfig.svg';
import { Typography } from 'components';
import { useSnack } from 'plugins/snack';
import { AppDispatch, RootState } from 'store';
import { fetchVideos } from 'store/inference';
import DataGridComponent from 'components/DataGrid';
import { InferenceMode } from 'schemas/index';
import moment from 'moment';
import { useDeleteInferenceVideos } from 'api/sdk';

interface IProps {
  selected: string[];
  checkStatus(temp: any): void;
  rows: any[];
  loading: boolean;
  isVideoUploading: boolean;
  setVideoModalOpen: any;
  setSelected: (val: string[]) => void;
  handleButtonClick?: any;
  setVideoName: any;
  setCurrentVideo: any;
  setHeatmapVideo: any;
  setVideoReportQuery: any;
  setIsInferedVideo: any;
}

const ActionToolTip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black
  }
}));

function CircularProgressWithIcon(
  props: CircularProgressProps & { value: number }
) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} color="inherit" />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <FileUploadIcon />
      </Box>
    </Box>
  );
}

const VideoStatus = React.memo(
  ({
    status,
    uploadProgress = 0,
    processProgress = 0,
    fail_reason = '',
    checkStatus,
    checkAllUploads,
    rows
  }: {
    status: string;
    uploadProgress?: number;
    processProgress?: number;
    fail_reason?: string;
    setDisableSelect?: any;
    checkAllUploads?: any;
    checkStatus(temp: any): void;
    rows: any;
  }) => {
    useEffect(() => {
      checkAllUploads?.();
      checkStatus?.(rows);
    }, [status, rows, checkAllUploads, checkStatus]);

    switch (status) {
      case 'UPLOADING':
        return (
          <Stack direction={'column'}>
            <div>
              <ActionToolTip title={'Uploading...'}>
                <CircularProgressWithIcon value={uploadProgress} />
              </ActionToolTip>
            </div>
          </Stack>
        );
      case 'UPLOADED':
        return (
          <Stack direction={'column'}>
            <div>
              <ActionToolTip title={'Uploaded'}>
                <IconButton disableRipple style={{ color: 'blue' }}>
                  <CloudDoneIcon />
                </IconButton>
              </ActionToolTip>
            </div>
          </Stack>
        );
      case 'QUEUED':
        return (
          <Stack direction={'column'}>
            <div>
              <ActionToolTip title={'Queued'}>
                <IconButton disableRipple style={{ color: 'orange' }}>
                  <AddToQueueIcon />
                </IconButton>
              </ActionToolTip>
            </div>
          </Stack>
        );
      case 'PROCESSING':
        return (
          <Stack direction={'column'}>
            <div>
              <ActionToolTip title={'Processing...'}>
                <IconButton disableRipple style={{ color: 'orange' }}>
                  <CircularWithValueLabel
                    variant={
                      processProgress === 0 ? 'indeterminate' : 'determinate'
                    }
                    value={processProgress}
                    size={30}
                  />
                </IconButton>
              </ActionToolTip>
            </div>
          </Stack>
        );
      case 'COMPLETED':
        return (
          <Stack direction={'column'}>
            <div>
              <ActionToolTip title={'Completed'}>
                <IconButton
                  disableRipple
                  style={{ color: 'green', alignContent: 'center' }}
                >
                  <CheckCircleIcon />
                </IconButton>
              </ActionToolTip>
            </div>
          </Stack>
        );
      case 'FAILED':
        return (
          <Stack direction={'column'}>
            <div>
              <ActionToolTip title={fail_reason}>
                <IconButton disableRipple style={{ color: 'red' }}>
                  <CancelRoundedIcon />
                </IconButton>
              </ActionToolTip>
            </div>
          </Stack>
        );
      default:
        return null;
    }
  },
  (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
      prevProps.status === nextProps.status &&
      prevProps.uploadProgress === nextProps.uploadProgress &&
      prevProps.processProgress === nextProps.processProgress
    );
  }
);

const VideoAction = React.memo(
  ({
    onDelete,
    onPlay,
    status,
    loading,
    video_id,
    disableSelect,
    isVideoUploading
  }: {
    video_id: string;
    status: string;
    loading: boolean;
    onDelete: () => void;
    onPlay?: () => void;
    isVideoUploading: boolean;
    disableSelect: boolean;
  }) => {
    return (
      <Stack direction={'row'} alignItems={'center'}>
        <div>
          <ActionToolTip title={'Play'}>
            <IconButton onClick={onPlay} disabled={status !== 'COMPLETED'}>
              <PlayArrowIcon />
            </IconButton>
          </ActionToolTip>
        </div>
        <div>
          <ActionToolTip title={'Delete'}>
            <DeleteAction
              onDelete={onDelete}
              loading={loading}
              disabled={
                status === 'UPLOADING' ||
                status === 'PROCESSING' ||
                isVideoUploading ||
                disableSelect
              }
              video_id={video_id}
            />
          </ActionToolTip>
        </div>
      </Stack>
    );
  }
);

const AnnotationTable: React.FC<IProps> = ({
  selected,
  rows = [],
  loading = false,
  isVideoUploading,
  setSelected,
  checkStatus,
  setVideoModalOpen,
  handleButtonClick,
  setVideoName,
  setCurrentVideo,
  setHeatmapVideo,
  setVideoReportQuery,
  setIsInferedVideo
}) => {
  const currentConfigId = useSelector(
    (state: RootState) => state?.configuration.configuration
  );
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const { isLoading, mutateAsync } = useDeleteInferenceVideos();
  const [disableSelect, setDisableSelect] = useState(false); // Changed to false by default
  const dispatch = useDispatch<AppDispatch>();
  const snack = useSnack();

  // Check if there are any uploading videos initially and when rows change
  useEffect(() => {
    const hasUploadingVideos = rows.some(row => row.status === 'UPLOADING');
    setDisableSelect(hasUploadingVideos);
  }, [rows]);

  const checkAllUploads = useCallback(() => {
    const hasUploadingVideos = rows.some(row => row.status === 'UPLOADING');
    setDisableSelect(hasUploadingVideos);
  }, [rows]);

  // Memoize the columns definition to prevent unnecessary re-renders
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: '',
        headerClassName: `super-app-theme--header no-sort-icon MuiDataGrid-columnHeader-sortIcon-hidden`,
        flex: 0.2,
        resizable: false,
        disableReorder: true,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        renderHeader: () => {
          const selectableRows = rows.filter(row =>
            ['COMPLETED', 'UPLOADED', 'FAILED'].includes(row.status)
          );
          const isAllSelected =
            selected.length === selectableRows.length &&
            selectableRows.length > 0;

          return (
            <ActionToolTip title="Select All">
              <Check
                version="light"
                checked={isAllSelected}
                onChange={(e: any) => {
                  e.preventDefault();
                  if (isAllSelected) {
                    // Deselect all
                    setSelected([]);
                  } else {
                    // Select all selectable rows
                    setSelected(selectableRows.map(row => String(row.id)));
                  }
                }}
                disabled={disableSelect || selectableRows.length === 0}
              />
            </ActionToolTip>
          );
        },
        renderCell: (params: GridCellParams) => {
          const isRowSelectable = ['COMPLETED', 'UPLOADED', 'FAILED'].includes(
            params.row.status
          );
          const isSelected = selected.includes(String(params.value));

          return (
            <ActionToolTip
              title={
                isRowSelectable
                  ? 'Select'
                  : params.row.status === 'FAILED'
                  ? 'Failed videos cannot be selected'
                  : 'Only completed or uploaded videos can be selected'
              }
            >
              <Check
                version="dark"
                checked={isSelected}
                onChange={(e: any) => {
                  e.preventDefault();
                  if (isRowSelectable) {
                    if (isSelected) {
                      setSelected(
                        selected.filter(val => val !== String(params.value))
                      );
                    } else {
                      setSelected([...selected, String(params.value)]);
                    }
                  }
                }}
                disabled={disableSelect || !isRowSelectable}
              />
            </ActionToolTip>
          );
        }
      },
      {
        field: 'name',
        headerName: 'Video Name',
        headerClassName: 'super-app-theme--header',
        flex: 1,
        renderCell: (params: any) => {
          const record = params.row;
          const isClickable =
            params.row.status !== 'UPLOADING' &&
            (params.row.status !== 'FAILED' || record.video_url !== null);

          return (
            <h2
              style={{
                textDecoration: isClickable ? 'underline' : 'none',
                cursor: isClickable ? 'pointer' : 'default',
                fontSize: '14px',
                fontWeight: 400,
                margin: 0,
                padding: 0
              }}
              onClick={() => {
                if (isClickable) {
                  setVideoName(params.value?.toString());
                  setCurrentVideo(params.row.video_url);
                  setHeatmapVideo(params.row.output_video_url);
                  setVideoReportQuery(params.row);
                  setVideoModalOpen(true);
                }
              }}
            >
              {params.value?.toString()}
            </h2>
          );
        }
      },
      {
        field: 'created_at',
        headerName: 'Uploaded At',
        headerClassName: 'super-app-theme--header',
        flex: 0.6,
        filterable: false,
        renderCell: (params: any) =>
          params?.value && params?.value !== '-'
            ? moment.utc(params.value).local().format('MMM DD, YYYY, h:mm a')
            : '-'
      },
      {
        field: 'inference_end_time',
        headerName: 'Inferred At',
        headerClassName: 'super-app-theme--header',
        flex: 0.6,
        filterable: false,
        renderCell: (params: any) =>
          params?.value && params?.value !== '-'
            ? moment.utc(params.value).local().format('MMM DD, YYYY, h:mm a')
            : '-'
      },
      {
        field: 'model',
        headerName: 'Model Used',
        headerClassName: 'super-app-theme--header',
        flex: 0.6,
        renderCell: (params: any) => (params?.value ? params.value : '-')
      },
      {
        field: 'seq_check',
        headerName: 'Sequence',
        headerClassName: 'super-app-theme--header',
        flex: 0.6,
        renderCell: (params: GridCellParams) => (
          <>
            {params.value?.toString() === 'Valid' ? (
              <p
                style={{
                  color: 'green',
                  fontSize: 12,
                  fontWeight: 'bold',
                  margin: 0,
                  padding: 0
                }}
              >
                Valid
              </p>
            ) : params.value?.toString() === 'Invalid' ? (
              <p
                style={{
                  color: 'red',
                  fontSize: 12,
                  fontWeight: 'bold',
                  margin: 0,
                  padding: 0
                }}
              >
                Invalid
              </p>
            ) : (
              <p
                style={{
                  color: 'orange',
                  fontSize: 12,
                  fontWeight: 'bold',
                  margin: 0,
                  padding: 0
                }}
              >
                -
              </p>
            )}
          </>
        )
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.4,
        headerClassName: 'super-app-theme--header',
        disableReorder: true,
        disableColumnMenu: true,
        filterable: false,
        renderCell: (params: GridCellParams) => (
          <VideoStatus
            status={params.value as string}
            uploadProgress={params.row.upload_progress || 0}
            processProgress={params.row.process_progress || 0}
            fail_reason={params.row.fail_reason as string}
            checkAllUploads={checkAllUploads}
            checkStatus={checkStatus}
            rows={rows}
          />
        )
      },
      {
        field: 'action',
        headerName: 'Action',
        headerClassName: 'super-app-theme--header',
        filterable: false,
        disableReorder: true,
        disableColumnMenu: true,
        flex: 0.6,
        renderCell: (params: GridCellParams) => (
          <VideoAction
            loading={isLoading && deletingVideoId === params.row.id}
            status={params.row.status}
            onPlay={() => {
              setVideoName(params.row.name.toString());
              setCurrentVideo(params.row.video_url);
              setHeatmapVideo(params.row.output_video_url);
              setIsInferedVideo(true);
              setVideoReportQuery(params.row);
              setVideoModalOpen(true);
            }}
            disableSelect={disableSelect}
            isVideoUploading={isVideoUploading}
            video_id={params.row.id || ''}
            onDelete={async () => {
              setDeletingVideoId(params.row.id);
              try {
                await mutateAsync({
                  videoId: params.row.id
                }).then(() => {
                  dispatch(
                    fetchVideos({
                      config_id: currentConfigId || '',
                      mode: InferenceMode.OFFLINE
                    })
                  ).then(() => {
                    setSelected([]);
                    snack({
                      message: 'Video deleted successfully',
                      severity: 'success'
                    });
                  });
                });
              } finally {
                setDeletingVideoId(null);
              }
            }}
          />
        )
      }
    ],
    [
      rows,
      selected,
      disableSelect,
      isLoading,
      deletingVideoId,
      checkAllUploads,
      checkStatus,
      isVideoUploading,
      currentConfigId,
      dispatch,
      mutateAsync,
      setSelected,
      snack,
      setVideoName,
      setCurrentVideo,
      setHeatmapVideo,
      setIsInferedVideo,
      setVideoReportQuery,
      setVideoModalOpen
    ]
  );

  return (
    <>
      {currentConfigId !== 'Select Configuration' ? (
        rows?.length > 0 ? (
          <DataGridComponent
            columns={columns}
            rows={rows}
            loading={loading}
            disableRowSelectionOnClick={true}
            height="60vh"
          />
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '65vh'
              }}
            >
              <div
                style={{
                  borderStyle: 'dashed'
                }}
              >
                <IconButton
                  onClick={() => {
                    if (currentConfigId !== 'Select Configuration') {
                      handleButtonClick();
                    }
                  }}
                  style={{ width: '20vw', padding: '20px', color: 'black' }}
                >
                  <SystemUpdateAltIcon />
                  <div style={{ marginLeft: '20px' }}>Import Videos</div>
                </IconButton>
              </div>
            </div>
          </Paper>
        )
      ) : (
        <Stack
          sx={{ height: '650px' }}
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <img
            src={SelectConfig}
            alt="select_config"
            style={{ width: '260px' }}
          />
          <Typography sx={{ fontSize: '24px', color: '#707070' }}>
            Select configuration type to view Inference
          </Typography>
        </Stack>
      )}
    </>
  );
};

// Custom equality function for React.memo
const arePropsEqual = (prevProps: IProps, nextProps: IProps) => {
  // Deep compare rows to check for changes in status or progress
  if (prevProps.rows.length !== nextProps.rows.length) {
    return false;
  }

  // Check for status and progress changes
  for (let i = 0; i < prevProps.rows.length; i++) {
    const prevRow = prevProps.rows[i];
    const nextRow = nextProps.rows[i];

    if (
      prevRow.id !== nextRow.id ||
      prevRow.status !== nextRow.status ||
      prevRow.upload_progress !== nextRow.upload_progress ||
      prevRow.process_progress !== nextRow.process_progress
    ) {
      return false;
    }
  }

  // Check other important props
  return (
    prevProps.selected.length === nextProps.selected.length &&
    prevProps.selected.every((id, index) => id === nextProps.selected[index]) &&
    prevProps.loading === nextProps.loading &&
    prevProps.isVideoUploading === nextProps.isVideoUploading
  );
};

export default React.memo(AnnotationTable, arePropsEqual);
